/**
 * Date preset utilities for analytics date range selection
 * Provides 11 timezone-aware date presets and default date range functionality
 */

import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { createTimezoneAwareDate } from './timezoneUtils';

// Local DateRange type definition
export type DateRange = {
  from: Date;
  to: Date;
};

// Helper to get date presets in a specific timezone
export const getDatePresetsForTimezone = (timezone: string) => [
  {
    label: "Today",
    dateRange: {
      from: createTimezoneAwareDate(new Date(), timezone),
      to: createTimezoneAwareDate(new Date(), timezone, true),
    },
  },
  {
    label: "Current Week",
    dateRange: {
      from: createTimezoneAwareDate(startOfWeek(toZonedTime(new Date(), timezone) as Date, { weekStartsOn: 1 }), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Previous Week",
    dateRange: {
      from: createTimezoneAwareDate(
        startOfWeek(subDays(toZonedTime(new Date(), timezone) as Date, 7), { weekStartsOn: 1 }),
        timezone
      ),
      to: createTimezoneAwareDate(
        endOfWeek(subDays(toZonedTime(new Date(), timezone) as Date, 7), { weekStartsOn: 1 }),
        timezone,
        true
      ),
    },
  },
  {
    label: "Last 7 Days",
    dateRange: {
      from: createTimezoneAwareDate(subDays(toZonedTime(new Date(), timezone) as Date, 7), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Current Month",
    dateRange: {
      from: createTimezoneAwareDate(startOfMonth(toZonedTime(new Date(), timezone) as Date), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Previous Month",
    dateRange: {
      from: createTimezoneAwareDate(
        startOfMonth(subMonths(toZonedTime(new Date(), timezone) as Date, 1)),
        timezone
      ),
      to: createTimezoneAwareDate(
        endOfMonth(subMonths(toZonedTime(new Date(), timezone) as Date, 1)),
        timezone,
        true
      ),
    },
  },
  {
    label: "Last 30 Days",
    dateRange: {
      from: createTimezoneAwareDate(subDays(toZonedTime(new Date(), timezone) as Date, 30), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Last 90 Days",
    dateRange: {
      from: createTimezoneAwareDate(subMonths(toZonedTime(new Date(), timezone) as Date, 3), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Last 6 Months",
    dateRange: {
      from: createTimezoneAwareDate(subMonths(toZonedTime(new Date(), timezone) as Date, 6), timezone),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Year to Date",
    dateRange: {
      from: createTimezoneAwareDate(
        new Date((toZonedTime(new Date(), timezone) as Date).getFullYear(), 0, 1),
        timezone
      ),
      to: createTimezoneAwareDate(toZonedTime(new Date(), timezone) as Date, timezone, true),
    },
  },
  {
    label: "Previous Year",
    dateRange: {
      from: createTimezoneAwareDate(
        startOfYear(subYears(toZonedTime(new Date(), timezone) as Date, 1)),
        timezone
      ),
      to: createTimezoneAwareDate(
        endOfYear(subYears(toZonedTime(new Date(), timezone) as Date, 1)),
        timezone,
        true
      ),
    },
  },
];

// Default date range (last 30 days) - timezone-aware
export const getDefaultDateRange = (timezone = 'UTC'): DateRange => {
  const now = toZonedTime(new Date(), timezone) as Date;
  const thirtyDaysAgo = subDays(now, 30);

  return {
    from: createTimezoneAwareDate(thirtyDaysAgo, timezone),
    to: createTimezoneAwareDate(now, timezone, true),
  };
};

/** Rolling 12 months — default for report viewer datepicker */
export const getLastYearDateRange = (timezone = 'UTC'): DateRange => {
  const now = toZonedTime(new Date(), timezone) as Date;
  return {
    from: createTimezoneAwareDate(subYears(now, 1), timezone),
    to: createTimezoneAwareDate(now, timezone, true),
  };
};
